<template>
  <button
    class="walletConnect-btn"
    @click="handleButtonClick"
  >
    <WalletconnectIcon />
  </button>
</template>

<script setup>
import { computed } from "vue";
import WalletconnectIcon from "@/components/icons/Walletconnect.vue";
import * as EvmChains from "@/assets/data/evm_chains.json";
import { ethers } from "ethers";
import Web3Modal from "web3modal";
import {getAllWalletConnectProviderOption, getSpecificWalletConnectProviderOption} from "@/composables/walletConnect/useWalletConnectProvider";
import { useGlobalStore } from "@/stores/globalStore";

const globalStore = useGlobalStore();

const props = defineProps({
  evmChainId: {
    type: Number,
    required: true,
    default: 1,
  },
  provider: {
    type: [String],
    required: false,
    default: []
  },
  desktop: {
    type: [String],
    required: false,
    default: []
  },
  mobile: {
    type: [String],
    required: false,
    default: []
  }
});

const emit = defineEmits(["updateWallet"]);

const metamaskInstalled = computed(() => {
  return window?.ethereum?.isMetaMask;
});

const isMetamaskConnected = computed(() => {
  return metamaskInstalled.value && window.ethereum.selectedAddress !== null;
});

const handleMetamaskAccountsSelectedEvent = (metaMaskAccount) => {
  const metaMaskWalletAddresss = metaMaskAccount[0];
  emit("update-wallet", metaMaskWalletAddresss);
};

const requestMetamaskAccount = () => {
  window.ethereum
    .request({ method: "eth_requestAccounts" })
    .then(handleMetamaskAccountsSelectedEvent)
    .catch((error) => {
      if (error.code === 4001) {
        // EIP-1193 userRejectedRequest error
        console.log("Please connect to MetaMask.");
      } else {
        console.error(error);
      }
    });
};

const getChainProfile = (chainId) => {
  const filteredChainProfile = EvmChains.default.filter((chain) => {
    return chain.chainId === parseInt(chainId, 16);
  });
  if (filteredChainProfile.length === 0) {
    return {};
  }
  const chainProfile = filteredChainProfile[0];
  chainProfile.chainId = `0x${chainProfile.chainId.toString(16)}`;
  return chainProfile;
};

const getProviderOptions = () => {
  let providerOptions = {}
  if (props.mobile.length > 0 || props.desktop.length > 0){
    providerOptions = getSpecificWalletConnectProviderOption(props.desktop, props.mobile);
  } else {
    providerOptions = getAllWalletConnectProviderOption();
  }
  
  // Add other provider here

  return providerOptions;
}

const handleButtonClick = async () => {
  // Handle button click
  const providerOptions = getProviderOptions();
  const theme = globalStore.getIsLightMode ? "light" : "dark";
  const web3Modal = new Web3Modal({
    network: "mainnet",
    cacheProvider: false,
    theme,
    providerOptions // required
  });

  const instance = await web3Modal.connect();

  const web3Provider = new ethers.providers.Web3Provider(instance);

  if (web3Provider.connection?.url === "metamask"){
    // Sepcial handling on Metamask to switch network
    try {
      await window.ethereum
        .request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: props.evmChainId }],
        })
        .then(requestMetamaskAccount);
    } catch ({ code }) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (code === 4902) {
        try {
          const newChainProfile = getChainProfile(props.evmChainId);
          await window.ethereum
            .request({
              method: "wallet_addEthereumChain",
              params: [newChainProfile],
            })
            .then(requestMetamaskAccount);
        } catch (defaultChainConnectionError) {
          // handle "add" error
          console.error(defaultChainConnectionError);
          console.error(
            "defaultChainConnectionError trying to connect to Ethereum Chain"
          );
        }
      } 
    } 
  } else {
    emit("update-wallet", web3Provider.provider?.accounts[0]);
    // In buy flow, we don't actually need to persist the connection. Disconnect instance after we are done.
    await instance.disconnect();
  }
};

</script>

<style lang="scss">
.walletConnect-btn {
  width: 6rem;
  aspect-ratio: 1 / 1;
  background: var(--input-background);
  border-radius: $border-radius;
  cursor: pointer;
  position: relative;
  border: 2px solid var(--input-background);
  transition: background 250ms;

  &:hover {
    background: var(--background);
  }

  &:active {
    background: rgb(255, 231, 208);
  }

  svg {
    width: 2.5rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
